import React, { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { apiGet } from '../../utils/apiFetch'
import apiPath from '../../utils/apiPath'
import { AiOutlineFilePdf } from 'react-icons/ai'
import { BiArrowBack } from 'react-icons/bi'

const DocumentCard = ({ label, url }) => {
  if (!url) return null;
  return (
    <div className="flex flex-col items-center justify-center p-6 bg-white border border-gray-200 rounded-xl shadow-sm hover:shadow-md transition-shadow">
      <AiOutlineFilePdf className="text-red-500 mb-4" size={48} />
      <h4 className="text-sm font-semibold text-gray-800 text-center mb-4 min-h-[40px] flex items-center justify-center">
        {label}
      </h4>
      <a 
        href={url}
        target="_blank"
        rel="noreferrer"
        className="w-full text-center py-2 px-4 bg-blue-50 text-blue-600 hover:bg-blue-600 hover:text-white rounded-lg text-sm font-medium transition-colors"
      >
        View Document
      </a>
    </div>
  )
}

const OnboardingCandidateDetails = () => {
  const { t } = useTranslation()
  const { id } = useParams()
  const navigate = useNavigate()
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchCandidate = async () => {
      try {
        setLoading(true)
        const res = await apiGet(`${apiPath.onboardingCandidateById}/${id}`)
        setData(res?.data?.data || null)
      } catch (error) {
        console.error('Error fetching candidate:', error)
      } finally {
        setLoading(false)
      }
    }
    fetchCandidate()
  }, [id])

  if (loading) {
    return <div className="p-6 text-center text-gray-500">{t('Loading...')}</div>
  }

  if (!data) {
    return <div className="p-6 text-center text-red-500">{t('Candidate not found.')}</div>
  }

  const docs = [
    { label: 'View All', url: data.mergedPdf?.url || data.pdfUrl || data.pdf_url },
    { label: 'Signed Offer Letter', url: data.signedOfferLetter },
    { label: 'National ID / Iqama', url: data.nationalIdIqama },
    { label: 'Updated CV', url: data.updatedCv },
    { label: 'Education Certificate', url: data.educationCertificate },
    { label: 'National Address', url: data.nationalAddress },
    { label: 'IBAN Certificate', url: data.ibanCertificate },
    { label: 'Training / Experience', url: data.trainingOrExperienceCertificate },
  ]

  return (
    <div className="p-6 max-w-7xl mx-auto min-h-screen">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <button 
          onClick={() => navigate(-1)}
          className="p-2 bg-white border border-gray-200 rounded-full hover:bg-gray-50 transition-colors"
          title="Back"
        >
          <BiArrowBack size={20} className="text-gray-600" />
        </button>
        <div>
          <h2 className="text-2xl font-bold text-gray-900">{t('Candidate Details')}</h2>
          <p className="text-sm text-gray-500 mt-1">Review onboarding information and submitted documents.</p>
        </div>
      </div>

      {/* Candidate Profile Info */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 mb-8 shadow-sm">
        <h3 className="text-lg font-semibold text-gray-800 border-b border-gray-100 pb-4 mb-4">
          Personal Information
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div>
            <p className="text-sm text-gray-500 mb-1">{t('O_FULL_NAME') || 'Full Name'}</p>
            <p className="text-base font-medium text-gray-900">{data.fullNameArabic || '-'}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500 mb-1">{t('O_EMAIL_ADDRESS') || 'Email Address'}</p>
            <p className="text-base font-medium text-gray-900">{data.email || '-'}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500 mb-1">{t('CAREERS_APPLIED_AT') || 'Applied At'}</p>
            <p className="text-base font-medium text-gray-900">
              {data.createdAt ? new Date(data.createdAt).toLocaleString() : '-'}
            </p>
          </div>
        </div>
      </div>

      {/* Documents Grid */}
      <div className="mb-8">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">
          Submitted Documents
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {docs.map((doc, index) => (
            <DocumentCard key={index} label={doc.label} url={doc.url} />
          ))}
        </div>
        {!docs.some(doc => doc.url) && (
          <div className="p-8 text-center bg-white border border-gray-200 rounded-xl">
            <p className="text-gray-500">No documents found for this candidate.</p>
          </div>
        )}
      </div>

    </div>
  )
}

export default OnboardingCandidateDetails
